import { FileIcon } from '@/assets/icons'
import React from 'react'
import BentoTile from './BentoTile'
import { FilesSection, TileState } from './core'
import EmptyState from './EmptyState'
import {
	FileThumb,
	FileThumbCell,
	FileThumbName,
	FileThumbRow,
	PdfThumb,
	PdfThumbLabel,
} from './FilesTile.styles'
import SectionTeaser from './SectionTeaser'

interface FilesTileProps {
	section: FilesSection
	/** Resolved tile state from the shared state machine (see `resolveTileState`). */
	state: TileState
	/** Navigate to the files page. */
	onNavigate: () => void
	/** Render a skeleton while the files query loads. */
	loading?: boolean
}

/**
 * The files bento tile: a horizontal row of three thumbnails with filenames.
 * Clicking it navigates to the files page, like every other tile.
 */
const FilesTile: React.FC<FilesTileProps> = ({ section, state, onNavigate, loading }) => {
	const icon = <FileIcon sx={{ fontSize: 16 }} />
	const { count, items } = section

	if (state === 'teaser') {
		return <SectionTeaser icon={icon} label="Files" count={count} colSpan={5} loading={loading} />
	}

	const body =
		state === 'detail' ? (
			<FileThumbRow>
				{items.map(file => (
					<FileThumbCell key={file.id}>
						{file.isPdf ? (
							<PdfThumb>
								<FileIcon />
								<PdfThumbLabel>PDF</PdfThumbLabel>
							</PdfThumb>
						) : (
							<FileThumb
								url={file.thumbnailUrl}
								alt=""
								fallbackIcon={<FileIcon />}
							/>
						)}
						<FileThumbName>{file.fileName}</FileThumbName>
					</FileThumbCell>
				))}
			</FileThumbRow>
		) : (
			<EmptyState
				icon={<FileIcon sx={{ fontSize: 32 }} />}
				text="No files yet — share receipts, bookings, and confirmations."
			/>
		)

	return (
		<BentoTile
			icon={icon}
			label="Files"
			count={count > 0 ? count : undefined}
			footer={count > 0 ? 'Open all files' : 'Go to files'}
			ariaLabel={count > 0 ? 'Open all files' : 'Go to files'}
			colSpan={5}
			onClick={onNavigate}
			loading={loading}
		>
			{body}
		</BentoTile>
	)
}

export default FilesTile
